import {Presenter} from '../support/with_presenter';
import {DragContext, Draggable, Draggables} from '../../business/drag_context';
import * as React from 'react';
import {SprintMover} from '../../business/sprint_mover';
import {format, isAfter, isBefore, isSameWeek} from 'date-fns';
import {Subscription} from 'rxjs';
import {EstimatesRepository} from '../../persistence/estimates_repository';
import {SprintCreater} from '../../business/sprint_creater';
import {Estimate} from '../../models/estimate';

export class SprintCellPresenter implements Presenter {
    public isSelected = false;
    public saving: boolean = false;
    public hours: number = 30;
    public isExtra: boolean = false;
    public modalVisible = false;
    public estimateId: number;
    public fromDate: Date | null = null;

    public get estimates(): Estimate[] {
        return this.estimatesRepository.all();
    }

    private subscription: Subscription | undefined;

    constructor(
        private date: Date,
        private employeeId: number,
        private dragContext: DragContext,
        private sprintMover: SprintMover,
        private estimatesRepository: EstimatesRepository,
        private sprintCreater: SprintCreater,
    ) {
        this.estimateId = this.estimatesRepository.all()[0].id;
    }

    public mount(): void {
        this.subscription = this.dragContext.currentlyDraggedUpdates.subscribe((currentlyDragged) => {
            this.handleCurrentlyDraggedUpdate(currentlyDragged);
        });
    }

    private handleCurrentlyDraggedUpdate(currentlyDragged: Draggable | null) {
        if (currentlyDragged === null) {
            this.isSelected = false;
        } else if (
            currentlyDragged.type === Draggables.CreateSprintButton &&
            currentlyDragged.employeeId === this.employeeId
        ) {
            if (currentlyDragged.till === null) {
                this.isSelected = false;
            } else {
                if (
                    isAfter(currentlyDragged.till, currentlyDragged.from) ||
                    isSameWeek(currentlyDragged.from, this.date)
                ) {
                    //Dragging to the right
                    this.isSelected =
                        isSameWeek(this.date, currentlyDragged.from) ||
                        (isBefore(currentlyDragged.from, this.date) && isAfter(currentlyDragged.till, this.date)) ||
                        isSameWeek(this.date, currentlyDragged.till);
                } else {
                    //Dragging to the left
                    this.isSelected =
                        isSameWeek(this.date, currentlyDragged.from) ||
                        (isBefore(currentlyDragged.till, this.date) && isAfter(currentlyDragged.from, this.date)) ||
                        isSameWeek(this.date, currentlyDragged.till);
                }
            }
        }
    }

    public unmount(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public async handleDrop() {
        if (
            this.dragContext.currentlyDragged !== null &&
            this.dragContext.currentlyDragged.type === Draggables.Sprint
        ) {
            const sprintId = this.dragContext.currentlyDragged.id;
            this.dragContext.clear();
            await this.sprintMover.move(sprintId, this.employeeId, this.date);
        }
        if (
            this.dragContext.currentlyDragged !== null &&
            this.dragContext.currentlyDragged.type === Draggables.CreateSprintButton
        ) {
            const fromDateFormatted = format(this.dragContext.currentlyDragged.from, 'yyyy-MM-dd');
            const tillDateFormatted = format(this.date, 'yyyy-MM-dd');
            if (fromDateFormatted !== tillDateFormatted) {
                this.fromDate = this.dragContext.currentlyDragged.from;
                this.modalVisible = true;
            }
            this.dragContext.clear();
        }
    }

    public handleDragOver(event: React.DragEvent<HTMLTableDataCellElement>) {
        if (this.dragContext.currentlyDragged !== null) {
            if (this.dragContext.currentlyDragged.type === Draggables.Sprint) {
                event.preventDefault();
                this.isSelected = true;
            }
            if (this.dragContext.currentlyDragged.type === Draggables.CreateSprintButton) {
                event.preventDefault();
                this.dragContext.register({
                    ...this.dragContext.currentlyDragged,
                    till: this.date,
                });
            }
        }
    }

    public handleDragLeave() {
        if (
            this.dragContext.currentlyDragged !== null &&
            this.dragContext.currentlyDragged.type === Draggables.Sprint
        ) {
            this.isSelected = false;
        }
    }

    public onHoursChange(hours: number) {
        this.hours = hours;
    }

    public onIsExtraChange(isExtra: boolean) {
        this.isExtra = isExtra;
    }

    public onEstimateIdChange(estimateId: number) {
        this.estimateId = estimateId;
    }

    public async onSaveClick() {
        if (this.fromDate === null) {
            return;
        }
        try {
            this.saving = true;
            await this.sprintCreater.create(
                this.employeeId,
                this.estimateId,
                this.hours,
                this.isExtra,
                this.fromDate,
                this.date,
            );
        } finally {
            this.saving = false;
        }
        this.modalVisible = false;
    }

    public onSubmit() {
        this.onSaveClick();
    }

    public onRequestModalClose() {
        this.modalVisible = false;
    }
}
